import { Objection, ObjectionType, ObjectionVariant, Role, User } from 'briefpoint-client';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { BriefcaseFill, ClipboardPlus, PencilFill, TrashFill } from 'react-bootstrap-icons';
import { useHistory, useLocation } from 'react-router-dom';
import { classes } from 'utils';
import { FormModal } from 'components/Modals/Modal';
import { useAuth } from 'hooks/useAuth';
import Loading from 'components/Loading';
import { isDeletable } from 'utils/objectionUtils';

interface Props {
  objections: Objection[];
  onDelete: (objectionId: number, variantId: number) => void;
  isResponses: boolean;
  users: Map<string, User>;
}

export default function ObjectionTable({ objections, onDelete, isResponses, users }: Props) {
  const [search, setSearch] = useState('');
  const { user } = useAuth()!;
  const [selectedObjection, setSelectedObjection] = useState<Objection>();
  const [allVariants, setAllVariants] = useState<ObjectionVariant[]>();
  const [allObjectionsSelected, setAllObjectionsSelected] = useState<boolean>(!isResponses);
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  const history = useHistory();
  const location = useLocation();
  const selection = new URLSearchParams(location.search).get('selection');

  useEffect(() => {
    if (objections && objections.length) {
      if (selectedObjection) {
        setSelectedObjection(objections.find((o) => o.id === selectedObjection.id));
      } else {
        setSelectedObjection(
          objections.filter(
            (objection) =>
              (!isResponses &&
                objection.objectionType !== ObjectionType.Response &&
                objection.objectionType !== ObjectionType.NoResponse) ||
              (isResponses &&
                (objection.objectionType === ObjectionType.Response ||
                  objection.objectionType === ObjectionType.NoResponse))
          )[0]
        );
      }
      var allTheVariants: ObjectionVariant[] = [];
      for (const objection of objections) {
        for (const variant of objection.variants) {
          allTheVariants.push(variant);
        }
      }
      setAllVariants(allTheVariants);
    }
  }, [isResponses, objections, selectedObjection]);

  useEffect(() => {
    if (objections && selection) {
      for (const objection of objections.filter(
        (objection) =>
          (!isResponses &&
            objection.objectionType !== ObjectionType.Response &&
            objection.objectionType !== ObjectionType.NoResponse) ||
          (isResponses &&
            (objection.objectionType === ObjectionType.Response ||
              objection.objectionType === ObjectionType.NoResponse))
      )) {
        if (objection.id === parseInt(selection)) {
          setAllObjectionsSelected(false);
          setSelectedObjection(objection);
        }
      }
    }
  }, [isResponses, objections, selection]);

  function setSelectionUrl(newUrl: number | null) {
    if (newUrl === null) {
      history.push({
        pathname: '/library',
        search: `?tab=${isResponses ? 'responses' : 'objections'}`,
      });
    } else {
      history.push({
        pathname: '/library',
        search: `?tab=${isResponses ? 'responses' : 'objections'}&selection=${newUrl}`,
      });
    }
  }

  async function handleConfirmDelete() {
    if (!selectedObjection) {
      return;
    }
    if (confirmRemove) {
      try {
        await onDelete(selectedObjection.id, confirmRemove);
        setConfirmRemove(null);
      } catch (error) {
        alert('Error deleting objection. It is probably in use.');
      }
    }
  }

  async function handleOnDeleteVariant(variant: ObjectionVariant) {
    if (!selectedObjection) {
      return;
    }
    setConfirmRemove(variant.id);
    // try {
    //   await onDelete(selectedObjection.id, variant.id);
    // } catch (error) {
    //   alert('Error deleting objection. It is probably in use.');
    // }
  }

  function getVariantsObjections(variant: ObjectionVariant) {
    if (objections) {
      for (const objection of objections) {
        if (variant.objectionId === objection.id) {
          return objection.name;
        }
      }
    }
    return '';
  }

  function getVariantsUser(variant: ObjectionVariant) {
    const variantUser = variant.ownerId ? users.get(variant.ownerId) : null;
    return (
      <Loading isLoading={!variantUser}>
        {variantUser && `${variantUser.firstName} ${variantUser.lastName}`}
      </Loading>
    )
  }

  function addVariant() {
    if (!allObjectionsSelected && selectedObjection) {
      history.push({
        pathname: `/library/addedit`,
        search: `?type=${selectedObjection.id}`,
      });
    } else {
      history.push({
        pathname: `/library/addedit`,
      });
    }
  }

  function editVariant(variant: ObjectionVariant, copy: boolean = false) {
    history.push({
      pathname: `/library/addedit`,
      search: `${copy ? '?copy=true' : ''}`,
      state: variant,
    });
  }

  return (
    <div className="step-container" style={{ gap: 0 }}>
      <div className="border-end" style={{ width: 250 }}>
        {!isResponses && (
          <div
            key={'all'}
            onClick={() => {
              setAllObjectionsSelected(true);
              setSelectedObjection(undefined);
              setSelectionUrl(null);
            }}
            className={classes('library-objection', {
              'library-objection-selected': allObjectionsSelected,
            })}
          >
            All Objections
          </div>
        )}
        {objections
          ?.filter(
            (objection) =>
              (!isResponses &&
                objection.objectionType !== ObjectionType.Response &&
                objection.objectionType !== ObjectionType.NoResponse) ||
              (isResponses &&
                (objection.objectionType === ObjectionType.Response ||
                  objection.objectionType === ObjectionType.NoResponse))
          )
          .map((o) => (
            <div
              key={o.id}
              onClick={() => {
                setAllObjectionsSelected(false);
                setSelectedObjection(o);
                setSelectionUrl(o.id);
              }}
              className={classes('library-objection', {
                'library-objection-selected': selectedObjection === o && !allObjectionsSelected,
              })}
            >
              {o.name}
            </div>
          ))}
      </div>
      <div className="flex-grow-1 ms-5">
        <Row className="mb-4">
          <Col xs="6">
            <div className="input-group mb-2">
              <div className="input-group-prepend">
                <div className="input-group-text">⌕</div>
              </div>
              <Form.Control
                value={search}
                className="form-control defense-search-input"
                placeholder="Search title"
                onChange={(e) => setSearch(e.target.value.toLowerCase())}
              />
            </div>
          </Col>
          <Col className="add-new-button" xs="6">
            <Button variant="primary" onClick={addVariant}>
              Create New
            </Button>
          </Col>
        </Row>
        <Row>
          <Table striped hover>
            <thead>
              <tr className="table-header">
                <th></th>
                <th className="table-header-cell" scope="">
                  Variant Title
                </th>
                <th className="table-header-cell" scope="col">
                  Objection Type
                </th>
                <th scope="col" className="table-header-cell">Created By</th>
                <th scope="col" className="table-header-cell">Default status</th>
                <th className="table-header-cell" scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {(allObjectionsSelected ? allVariants : selectedObjection?.variants)
                ?.filter((variant) => variant?.name.toLowerCase().includes(search) && !variant?.isDeleted)
                .map((variant) => (
                  <tr key={variant.id} className="table-row">
                    <td style={{ paddingLeft: 10 }}>{variant.sharedWithFirmId ? <BriefcaseFill title="Firm Standard" /> : ' '}</td>
                    <td className="table-cell">{variant.name}</td>
                    <td className="table-cell">{getVariantsObjections(variant)}</td>
                    <td className="table-cell">{getVariantsUser(variant)}</td>
                    <td className="table-cell">{variant.isDefault ? (variant.sharedWithFirmId ? 'Firm Default' : 'Personal Default') : '-'}</td>
                    <td className="table-cell actions-cell">
                      {(user?.role?.includes(Role.FirmAdmin) || !variant.sharedWithFirmId) &&
                        <>
                          {isDeletable(variant, allVariants, user!) &&
                            <TrashFill
                              onClick={() => handleOnDeleteVariant(variant)}
                              className="table-edit-item"
                              title='Delete'
                            />}
                          <PencilFill
                            onClick={() => editVariant(variant)}
                            className="table-edit-item"
                            title='Edit'
                          /></>}
                      <ClipboardPlus
                        onClick={() => editVariant(variant, true)}
                        className="table-edit-item"
                        title='Copy' />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Row>
      </div>
      <FormModal
        title={'Are you sure?'}
        cancelText="Cancel"
        confirmText="Yes, remove"
        show={confirmRemove != null}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleConfirmDelete}
      >
        <div>Are you sure you want to remove this variant? This cannot be undone.</div>
      </FormModal>
    </div>
  );
}
